import * as R from 'ramda';
import storage from 'local-storage-fallback';
import { TechnicalError } from '../common/errors';
import Log from '../common/logging';
import { Metadata } from '../services/metadata';

/** Static storage key names */
enum StorageKey {
  FlowId = 'flowId',
  UserMetadata = 'metadata',
  UserFeedbackComplete = 'userFeedbackComplete',
  CivicSignAuth = 'civicSignAuth',
  AcceptedTerms = 'acceptedTerms',
  AcceptedPrivacyPolicy = 'acceptedPrivacyPolicy',
  AcceptedBiometrics = 'acceptedBiometrics',
  CredentialExpired = 'credentialExpired',
}

/** Get the item for the given network address from storage */
const getStorageItemForNetwork = (keyName: StorageKey, networkAddress: string): string | null => {
  const key = `${keyName}:${networkAddress}`;
  const item = storage.getItem(key);
  return item;
};

/**
 * Get the item for the given network address and wallet address from storage
 * @param keyName
 * @param networkAddress
 * @param walletAddress
 * @returns
 */
const getStorageItemForWalletNetwork = (
  keyName: StorageKey,
  networkAddress: string,
  walletAddress: string | null,
): string | null => {
  const key = `${keyName}:${walletAddress || ''}:${networkAddress}`;
  const item = storage.getItem(key);
  return item;
};

/** Set the item for the given network address in storage */
const setStorageItemForNetwork = (keyName: StorageKey, networkAddress: string, value: string): void => {
  const key = `${keyName}:${networkAddress}`;
  storage.setItem(key, value);
};

/**
 * set the item for the given network address and wallet address from storage
 * the wallet address is optional as it's an optional query parameter
 * @param keyName
 * @param networkAddress
 * @param walletAddress
 * @param value
 */
const setStorageItemForWalletNetwork = (
  keyName: StorageKey,
  networkAddress: string,
  walletAddress: string | null,
  value: string,
): void => {
  const key = `${keyName}:${walletAddress || ''}:${networkAddress}`;
  storage.setItem(key, value);
};

/** Set the metadata for the given network address in storage */
function storeMetadata(data: Metadata): void {
  const { networkAddress } = data;
  if (!networkAddress) {
    Log.debug('Store metadata failed', new TechnicalError('Unable to store metadata, network address not set'));
    return;
  }
  const stringifiedData = JSON.stringify(data);
  setStorageItemForNetwork(StorageKey.UserMetadata, networkAddress, stringifiedData);
}

/** Get the metadata for the given network address in storage.
 *  If no stored metadata exists the returned metadata will still
 *  include the network address.
 */
function getStoredMetadata(networkAddress: string): Metadata {
  const metadataString = getStorageItemForNetwork(StorageKey.UserMetadata, networkAddress);
  if (R.isNil(metadataString) || metadataString === 'undefined') {
    return { networkAddress };
  }
  let metadata: Metadata;
  try {
    metadata = JSON.parse(metadataString);
  } catch (error) {
    Log.error('Getting stored metadata failed', new TechnicalError('Unable to parse stored metadata', error as Error));
    return { networkAddress };
  }

  const restoredMetadata: Metadata = {
    networkAddress,
    ...metadata,
  };
  const cleanedMetadata = R.reject(R.isNil)(restoredMetadata);
  return cleanedMetadata;
}

const storeCivicSignAuth = (civicSignAuth: string): void => {
  storage.setItem(StorageKey.CivicSignAuth, civicSignAuth);
};

const getCivicSignAuth = (): string | null => {
  const existingVal = storage.getItem(StorageKey.CivicSignAuth);
  return typeof existingVal === 'string' ? existingVal : null;
};

export {
  StorageKey,
  getStorageItemForNetwork,
  setStorageItemForNetwork,
  getStorageItemForWalletNetwork,
  setStorageItemForWalletNetwork,
  storeMetadata,
  getStoredMetadata,
  storeCivicSignAuth,
  getCivicSignAuth,
};
